import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import { getAppletsUrl } from '@cartwhl/common/utils';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import Input from '@cartwhl/ui/atoms/Input';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import GenericLoader from '@cartwhl/ui/feature/GenericLoader';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import GridIcon from '@cartwhl/ui/icons/GridIcon';
import ListIcon from '@cartwhl/ui/icons/ListIcon';
import SearchIcon from '@cartwhl/ui/icons/SearchIcon';
import SortIcon from '@cartwhl/ui/icons/SortIcon';
import SortIcon2 from '@cartwhl/ui/icons/SortIcon2';
import TrashIcon from '@cartwhl/ui/icons/TrashIcon';
import Button from '@cartwhl/ui/input/Button';
import StudioShellHeader from '@cartwhl/ui/layout/StudioShellHeader';

import {
  CreateProjectHero,
  DeleteProjectModal,
  ProjectSection,
  ProjectSectionError,
  ProjectSectionLoading,
} from 'components/ProjectManager';
import { projectStore } from 'stores';
import {
  clearSelectedProjects,
  deleteSelectedProjects,
} from 'stores/projectStore';

export default function Projects() {
  const { allProjects, selectedProjectIDs, deleteStatus } = useSnapshot(
    projectStore.state,
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterType, setFilterType] = useState<'all' | 'motion' | 'pose'>(
    'all',
  );
  const [sortType, setSortType] = useState<'date-created' | 'last-edited'>(
    'date-created',
  );
  const isMobile = useMediaQuery('(max-width: 640px)');

  useEffect(() => {
    if (allProjects.status === 'initial') {
      projectStore.loadProjects(sortType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProjects.status]);

  // When sortType changes, we don't need to reload - just re-sort existing projects, only load if we haven't loaded anything yet
  useEffect(() => {
    if (allProjects.status === 'initial') {
      projectStore.loadProjects(sortType);
    } else if (sortType === 'last-edited' && allProjects.data) {
      if (allProjects.data.nextToken) {
        projectStore.loadProjects(sortType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortType]);

  // Clear selections when clicking outside project cards anywhere on the page
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest('[data-project-card]') &&
        !target.closest('button') &&
        !target.closest('input') &&
        !target.closest('a') &&
        !target.closest('[role="button"]') &&
        !target.closest('[role="dialog"]') &&
        !target.closest('[role="menu"]')
      ) {
        clearSelectedProjects();
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  // Render header content based on mobile state
  const renderHeaderContent = () => {
    if (isMobile && selectedProjectIDs.length > 0) {
      return (
        <div className="bg-blue/10 dark:bg-blue/20 flex items-center gap-2 rounded-2xl px-3 py-2">
          <Button
            variant="tertiary"
            size="sm"
            icon={<CrossIcon />}
            onClick={(e) => {
              e.stopPropagation();
              clearSelectedProjects();
            }}
            className="h-6 w-6 p-0"
          />
          <Text variant="studio/body/sm" className="flex-1">
            {selectedProjectIDs.length} Selected
          </Text>
          <Tooltip text="Delete selected">
            <Button
              variant="tertiary"
              size="sm"
              icon={<TrashIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setIsDeleteModalOpen(true);
              }}
              className="h-6 w-6 p-0"
            />
          </Tooltip>
        </div>
      );
    }

    if (isMobile && isSearchExpanded) {
      return (
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1">
            <SearchIcon className="absolute top-1/2 left-3 h-6 w-6 -translate-y-1/2 text-black dark:text-white" />
            <Input
              name="search"
              type="text"
              placeholder="Search projects"
              className="w-full pr-10 pl-10"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
              autoFocus
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            icon={<CrossIcon />}
            onClick={() => {
              setSearchQuery('');
              setIsSearchExpanded(false);
            }}
            className="border-gray-30 dark:bg-gray-90 dark:border-gray-70 h-8 w-8 flex-shrink-0 border bg-white"
          />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Text as="h1" variant="studio/title/xl" className="px-2">
            Your Projects
          </Text>
          {!isMobile && selectedProjectIDs.length > 0 && (
            <div className="bg-blue/10 dark:bg-blue/20 flex items-center gap-2 rounded-2xl px-3 py-2">
              <Button
                variant="tertiary"
                size="sm"
                icon={<CrossIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelectedProjects();
                }}
                className="h-6 w-6 p-0"
              />
              <Text variant="studio/body/sm">
                {selectedProjectIDs.length} Selected
              </Text>
              <Tooltip text="Delete selected">
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={<TrashIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteModalOpen(true);
                  }}
                  className="h-6 w-6 p-0"
                />
              </Tooltip>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isMobile && (
            <div className="relative w-64">
              <Input
                name="search"
                type="text"
                placeholder="Search projects"
                className="pr-10"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchQuery(e.target.value)
                }
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer"
                >
                  <CrossIcon className="h-4 w-4 text-black dark:text-white" />
                </button>
              ) : (
                <SearchIcon className="absolute top-1/2 right-3 h-6 w-6 -translate-y-1/2 text-black dark:text-white" />
              )}
            </div>
          )}
          <Tooltip text={viewMode === 'grid' ? 'List view' : 'Grid view'}>
            <Button
              variant="secondary"
              size="sm"
              icon={
                viewMode === 'grid' ? (
                  <ListIcon className="h-6 w-6" />
                ) : (
                  <GridIcon className="h-6 w-6" />
                )
              }
              className="border-gray-30 dark:bg-gray-90 dark:border-gray-70 h-8 w-8 border bg-white"
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            />
          </Tooltip>
          {/* Filter and Sort buttons grouped together */}
          <div className="flex items-center" style={{ paddingTop: '5px' }}>
            <div className="relative inline-flex h-8 items-center">
              <Tooltip text="Filters">
                <div>
                  <Dropdown
                    trigger={
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<SortIcon className="h-6 w-6" />}
                        className={`border-gray-30 dark:border-gray-70 h-8 w-8 rounded-l-lg rounded-r-none border border-r ${
                          filterType === 'motion'
                            ? 'bg-[#c5f4d0] dark:bg-[#c5f4d0]'
                            : filterType === 'pose'
                              ? 'bg-[#d7c2fd] dark:bg-[#d7c2fd]'
                              : 'dark:bg-gray-90 bg-white'
                        }`}
                      />
                    }
                    width={150}
                  >
                    <div className="px-3 py-2">
                      <Text variant="studio/body/sm" className="text-secondary">
                        Filter by
                      </Text>
                    </div>
                    <Dropdown.Item
                      onClick={() => setFilterType('all')}
                      justify="start"
                      className="relative !pr-0"
                    >
                      <span className="pl-2">All</span>
                      {filterType === 'all' && (
                        <div className="absolute top-1/2 right-3 h-2 w-2 -translate-y-1/2 rounded-full bg-black dark:bg-white" />
                      )}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setFilterType('motion')}
                      justify="start"
                      className="relative !pr-0"
                    >
                      <span className="pl-2">Motion</span>
                      {filterType === 'motion' && (
                        <div className="absolute top-1/2 right-3 h-2 w-2 -translate-y-1/2 rounded-full bg-black dark:bg-white" />
                      )}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setFilterType('pose')}
                      justify="start"
                      className="relative !pr-0"
                    >
                      <span className="pl-2">Pose</span>
                      {filterType === 'pose' && (
                        <div className="absolute top-1/2 right-3 h-2 w-2 -translate-y-1/2 rounded-full bg-black dark:bg-white" />
                      )}
                    </Dropdown.Item>
                  </Dropdown>
                </div>
              </Tooltip>
              {filterType === 'motion' && (
                <div className="bg-green absolute -top-0.5 -left-0.5 h-2 w-2 rounded-full" />
              )}
              {filterType === 'pose' && (
                <div className="bg-violet absolute -top-0.5 -left-0.5 h-2 w-2 rounded-full" />
              )}
            </div>
            <Tooltip text="Sort">
              <div>
                <Dropdown
                  trigger={
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<SortIcon2 className="h-6 w-6" />}
                      className="border-gray-30 dark:bg-gray-90 dark:border-gray-70 h-8 w-8 rounded-l-none rounded-r-lg border border-l-0 bg-white"
                    />
                  }
                  width={150}
                >
                  <div className="px-3 py-2">
                    <Text variant="studio/body/sm" className="text-secondary">
                      Sort by
                    </Text>
                  </div>
                  <Dropdown.Item
                    onClick={() => setSortType('date-created')}
                    justify="start"
                    className="relative !pr-0"
                  >
                    <span className="pl-2">Date created</span>
                    {sortType === 'date-created' && (
                      <div className="absolute top-1/2 right-3 h-2 w-2 -translate-y-1/2 rounded-full bg-black dark:bg-white" />
                    )}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => setSortType('last-edited')}
                    justify="start"
                    className="relative !pr-0"
                  >
                    <span className="pl-2">Last edited</span>
                    {sortType === 'last-edited' && (
                      <div className="absolute top-1/2 right-3 h-2 w-2 -translate-y-1/2 rounded-full bg-black dark:bg-white" />
                    )}
                  </Dropdown.Item>
                </Dropdown>
              </div>
            </Tooltip>
          </div>
          {/* Mobile: Search icon button */}
          {isMobile && (
            <Button
              variant="secondary"
              size="sm"
              icon={<SearchIcon className="h-6 w-6" />}
              className="border-gray-30 dark:bg-gray-90 dark:border-gray-70 h-8 w-8 border bg-white"
              onClick={() => setIsSearchExpanded(true)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-primary flex min-h-dvh flex-col">
      <StudioShellHeader
        title="Canvas"
        homeHref={getAppletsUrl()}
        homeAriaLabel="App dashboard"
      />

      <main className="flex flex-1 flex-col">
      <CreateProjectHero />

      {/* Show loading modal while loading first batch (50 projects) for "last-edited" sorting */}
      {/* Hide when we have 50+ projects OR all projects are loaded (no nextToken) */}
      {sortType === 'last-edited' &&
        allProjects.status === 'pending' &&
        allProjects.data &&
        allProjects.data.items.length < 50 &&
        (allProjects.data.nextToken !== undefined ||
          allProjects.data.items.length === 0) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <GenericLoader
              loadingState={{
                text: 'Loading projects...',
                type: 'PROCESSING',
                buttons: (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setSortType('date-created')}
                    className="mt-4 -mb-4"
                  >
                    Cancel
                  </Button>
                ),
              }}
            />
          </div>
        )}
      {deleteStatus === 'pending' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <GenericLoader
            loadingState={{
              text: 'Deleting...',
              type: 'PROCESSING',
            }}
          />
        </div>
      )}

      <div className="border-gray-30 dark:border-gray-75 mt-12 flex flex-col gap-4 border-t px-5 pt-10 pb-32 xl:container">
        <div className="dark:bg-gray-90 sticky top-0 z-40 -mx-5 -mt-10 bg-white px-5 pt-10 pb-4">
          {renderHeaderContent()}
        </div>

        <div className="divide-gray-30 dark:divide-gray-75 relative flex flex-col divide-y">
          {allProjects.status === 'initial' ? (
            <ProjectSectionLoading />
          ) : allProjects.status === 'error' ? (
            <ProjectSectionError
              error={allProjects.error}
              sortType={sortType}
            />
          ) : (
            <ProjectSection
              searchQuery={searchQuery}
              viewMode={viewMode}
              filterType={filterType}
              sortType={sortType}
            />
          )}
        </div>

        {(allProjects.status === 'ready' ||
          allProjects.status === 'pending') && (
          <div className="flex justify-center py-6">
            <InfiniteLoadingTrigger
              loading={allProjects.status === 'pending'}
              done={
                allProjects.status === 'ready' &&
                (sortType === 'date-created'
                  ? allProjects.data != null &&
                    allProjects.data.nextToken === undefined
                  : true)
              }
              onEnter={() => {
                if (sortType === 'date-created') {
                  projectStore.loadProjects(sortType);
                }
              }}
            />
          </div>
        )}
      </div>

      <DeleteProjectModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onConfirm={deleteSelectedProjects}
        projectCount={selectedProjectIDs.length}
      />
      </main>
    </div>
  );
}
